import React, { ReactElement, ReactNode, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Box, FormLabel, Typography } from '@mui/material'
import { api } from '../common/api'
import { selectedSiteSelector } from '../authentication/redux'
import { useFetchData } from '../common/servercall/useFetchData'
import { FormSection } from '../common/components/SectionedForm'
import FormGroup from '../common/form/FormGroup'
import FormRow from '../common/form/FormRow'
import FormFieldBox from '../common/form/FormFieldBox'
import { GeneralForm } from './GeneralForm'
import { DEFAULT_ANNOUNCEMENT } from '../common/util/helpers'
import { formatPhoneNumber } from '../common/util/formatters'
import { SiteGeneral } from '../app/types'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import { useFormSubmissionButtons } from '../common/form/useFormSubmissmionButtons'

export const useGeneralSection = (): FormSection<SiteGeneral> => {
    const { t } = useTranslation()
    const site = useSelector(selectedSiteSelector)
    const { buildFormButtons } = useFormSubmissionButtons(false)

    const fetchGeneral = useCallback(async (): Promise<SiteGeneral | undefined> => {
        if (!site) return
        const { data } = await api.get<SiteGeneral>(`/api/site/${site.productId}`)
        return data
    }, [site])

    const { data, isFetching, error, refetch } = useFetchData<SiteGeneral>(fetchGeneral)

    const renderGeneralForm = useCallback(
        (general: SiteGeneral, cancelEditButton: ReactElement, onSucceeded: () => void): ReactNode =>
            <GeneralForm general={general} buildFormButtons={buildFormButtons(cancelEditButton)} onSuccess={() => { refetch(); onSucceeded() }} />,
        [refetch, buildFormButtons],
    )

    const renderField = useCallback(
        (label: string, value: ReactNode): ReactNode => (
            <FormFieldBox>
                <Box>
                    <FormLabel>{label}</FormLabel>
                </Box>
                <Box style={{ wordBreak: 'break-word', paddingRight: 3 }}>
                    <Typography variant={'body1'} component={'div'}>
                        {value}
                    </Typography>
                </Box>
            </FormFieldBox>
        ),
        [],
    )

    const renderDetails = useCallback(
        (general: SiteGeneral): ReactNode => (
            <FormGroup fullWidth={true}>
                <FormRow>
                    {renderField(t('site.cards.general.alias'), general.alias)}
                    {renderField(t('site.cards.general.main-number'), formatPhoneNumber(general.mainNumber))}
                </FormRow>
                <FormRow>
                    {renderField(
                        t('site.cards.general.music-on-hold'),
                        !general.musicOnHoldFileName || general.musicOnHoldFileName === DEFAULT_ANNOUNCEMENT ? t('general.default') : general.musicOnHoldFileName,
                    )}
                </FormRow>
            </FormGroup>
        ),
        [t, renderField],
    )

    return {
        title: t('general.general'),
        data,
        isFetching,
        error,
        refetch,
        renderDetails,
        renderForm: renderGeneralForm,
        enabled: () => true,
        icon: () => <LocationOnIcon />,
    }
}
